/**
 * Quick G14 Calculator Page
 *
 * Public page (no auth required) for a quick G14 dynamic tariff analysis.
 * Users answer 3 questions → get immediate cost estimate + recommendation.
 *
 * Route: /:osd/kalkulatory/g14-szybki (TauronOnlyRoute)
 */

import { useState, useMemo } from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  Stack,
  Slider,
  SegmentedControl,
  Select,
  Group,
  Badge,
  Divider,
  Loader,
  Alert,
  Button,
} from '@mantine/core';
import {
  IconSun,
  IconClock,
  IconMoon,
  IconInfoCircle,
  IconArrowRight,
} from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

import { Layout } from '../../shared/components/Layout';
import { useSEO } from '../../shared/hooks';
import { useAuth } from '../../contexts/AuthContext';
import { useOSD } from '../../contexts/OSDContext';
import { useG14QuickCalc } from './hooks/useG14QuickCalc';
import { G14QuickResult } from './components/G14QuickResult';
import type { ConsumptionPreset } from './constants/consumptionPresets';

/** Comparison tariff options with approximate total rates (PLN/kWh incl. distribution) */
const COMPARISON_TARIFFS = [
  { value: 'G11', label: 'G11 - Jednolita', rate: 0.89 },
  { value: 'G12', label: 'G12 - Dwustrefowa', rate: 0.82 },
  { value: 'G12w', label: 'G12w - Weekendowa', rate: 0.80 },
];

/** Zone colors for hourly price chart */
function getBarColor(price: number): string {
  if (price <= 0.30) return '#22c55e'; // GREEN
  if (price <= 0.45) return '#84cc16'; // LIGHT_GREEN
  if (price <= 0.65) return '#eab308'; // YELLOW
  return '#ef4444'; // RED
}

export function QuickG14Calculator() {
  useSEO({
    title: 'Szybki kalkulator G14 - Taryfa dynamiczna',
    description: 'Sprawdź w 30 sekund czy taryfa G14 (dynamiczna) się opłaca. Szybka analiza na podstawie Twojego profilu zużycia.',
  });

  const { isAuthenticated } = useAuth();
  const { getOSDPath } = useOSD();

  const currentYear = new Date().getFullYear();

  const [monthlyKwh, setMonthlyKwh] = useState(300);
  const [preset, setPreset] = useState<ConsumptionPreset>('even');
  const [comparisonTariff, setComparisonTariff] = useState('G11');

  const { stats, loading, error, calculate } = useG14QuickCalc(currentYear);

  const comparisonRate = useMemo(
    () => COMPARISON_TARIFFS.find((t) => t.value === comparisonTariff)?.rate ?? 0.89,
    [comparisonTariff]
  );

  const result = useMemo(
    () => calculate(monthlyKwh, preset, comparisonRate),
    [calculate, monthlyKwh, preset, comparisonRate]
  );

  // Hourly price chart data
  const chartData = useMemo(() => {
    if (!stats?.hourlyAvgPriceKwh) return [];
    return stats.hourlyAvgPriceKwh.map((price, hour) => ({
      hour: `${hour}`,
      price: Math.round(price * 10000) / 10000,
    }));
  }, [stats]);

  return (
    <Layout isAuthenticated={isAuthenticated}>
      <Container size="md" py="xl">
        <Stack gap="xl">
          <Paper shadow="md" radius="lg" p="xl" withBorder>
            <Stack gap="lg">
              {/* Header */}
              <div>
                <Badge color="red" variant="light" size="sm" mb="xs">
                  Szybka analiza G14
                </Badge>
                <Title order={2}>Sprawdź czy taryfa G14 się opłaca</Title>
                <Text c="dimmed" size="sm">
                  Odpowiedz na 3 pytania i zobacz szacunkowy koszt taryfy dynamicznej
                </Text>
              </div>

              <Divider />

              {loading && (
                <Group justify="center" py="xl">
                  <Loader size="lg" />
                  <Text c="dimmed">Pobieranie danych cenowych...</Text>
                </Group>
              )}

              {error && !loading && (
                <Alert icon={<IconInfoCircle size={18} />} color="yellow" variant="light">
                  <Text size="sm">{error}</Text>
                  <Text size="xs" c="dimmed" mt="xs">
                    Dane G14 mogą nie być jeszcze dostępne. Skontaktuj się z administratorem.
                  </Text>
                </Alert>
              )}

              {stats && !loading && (
                <>
                  {/* Step 1: Monthly consumption */}
                  <div>
                    <Group justify="space-between" mb="xs">
                      <Text fw={500}>1. Ile zużywasz prądu miesięcznie?</Text>
                      <Badge size="lg" variant="filled" color="red">
                        {monthlyKwh} kWh
                      </Badge>
                    </Group>
                    <Slider
                      value={monthlyKwh}
                      onChange={setMonthlyKwh}
                      min={100}
                      max={2000}
                      step={10}
                      marks={[
                        { value: 100, label: '100 kWh' },
                        { value: 500, label: '500' },
                        { value: 1000, label: '1000' },
                        { value: 2000, label: '2000 kWh' },
                      ]}
                      color="red"
                      size="lg"
                      mb="md"
                    />
                  </div>

                  {/* Step 2: Usage pattern */}
                  <div>
                    <Text fw={500} mb="xs">2. Kiedy głównie zużywasz prąd?</Text>
                    <SegmentedControl
                      value={preset}
                      onChange={(v) => setPreset(v as ConsumptionPreset)}
                      fullWidth
                      size="md"
                      data={[
                        {
                          value: 'day',
                          label: (
                            <Group gap="xs" wrap="nowrap">
                              <IconSun size={16} />
                              <span>Dzień</span>
                            </Group>
                          ),
                        },
                        {
                          value: 'even',
                          label: (
                            <Group gap="xs" wrap="nowrap">
                              <IconClock size={16} />
                              <span>Równomiernie</span>
                            </Group>
                          ),
                        },
                        {
                          value: 'night',
                          label: (
                            <Group gap="xs" wrap="nowrap">
                              <IconMoon size={16} />
                              <span>Noc/wieczór</span>
                            </Group>
                          ),
                        },
                      ]}
                      color="red"
                    />
                  </div>

                  {/* Step 3: Comparison tariff */}
                  <div>
                    <Text fw={500} mb="xs">3. Porównaj z taryfą:</Text>
                    <Select
                      value={comparisonTariff}
                      onChange={(v) => v && setComparisonTariff(v)}
                      data={COMPARISON_TARIFFS.map((t) => ({
                        value: t.value,
                        label: t.label,
                      }))}
                      size="md"
                    />
                  </div>

                  <Divider />

                  {/* Result */}
                  {result && (
                    <G14QuickResult
                      result={result}
                      comparisonTariffName={
                        COMPARISON_TARIFFS.find((t) => t.value === comparisonTariff)?.label ?? 'G11'
                      }
                    />
                  )}
                </>
              )}
            </Stack>
          </Paper>

          {/* Hourly Price Chart */}
          {chartData.length > 0 && (
            <Paper shadow="sm" radius="lg" p="xl" withBorder>
              <Text fw={500} mb="md">
                Średnie ceny RCE w ciągu doby ({currentYear})
              </Text>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData}>
                  <XAxis
                    dataKey="hour"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(h) => `${h}:00`}
                    interval={2}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v) => `${v.toFixed(2)}`}
                    label={{ value: 'zł/kWh', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
                  />
                  <Tooltip
                    formatter={(value: number) => [`${value.toFixed(4)} zł/kWh`, 'Cena RCE']}
                    labelFormatter={(h) => `Godzina ${h}:00`}
                  />
                  <Bar dataKey="price" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell key={index} fill={getBarColor(entry.price)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <Text size="xs" c="dimmed" ta="center" mt="xs">
                Kolory odpowiadają strefom PSE: zielona, jasnozielona, żółta, czerwona
              </Text>
            </Paper>
          )}

          {/* CTA */}
          <Stack gap="xs">
            <Button
              component={Link}
              to={getOSDPath('/kalkulatory/zaawansowany')}
              size="lg"
              rightSection={<IconArrowRight size={18} />}
              fullWidth
            >
              Dokładniejsze obliczenia z własnym profilem zużycia
            </Button>
            <Text size="xs" c="dimmed" ta="center">
              Pełna analiza z importem godzinowym, symulacją Monte Carlo i optymalizacją
            </Text>
          </Stack>
        </Stack>
      </Container>
    </Layout>
  );
}
